import React, { useEffect, useRef, useState } from "react";
import { RotateCcw, X } from "lucide-react";

/**
 * "Deleted X — Undo" toast (top-center). `item` = { key, label, durationMs }.
 * A new `key` restarts the countdown; callbacks are read through refs so a
 * parent re-render never resets the timer.
 */
const UndoToast = ({ item, onUndo, onDismiss }) => {
  const [left, setLeft] = useState(100);
  const undoRef = useRef(onUndo);
  const dismissRef = useRef(onDismiss);
  undoRef.current = onUndo;
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!item) return undefined;
    const start = Date.now();
    const dur = item.durationMs || 6000;
    setLeft(100);
    const id = setInterval(() => {
      const p = Math.max(0, 100 - ((Date.now() - start) / dur) * 100);
      setLeft(p);
      if (p <= 0) {
        clearInterval(id);
        dismissRef.current && dismissRef.current();
      }
    }, 100);
    return () => clearInterval(id);
  }, [item]);

  if (!item) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[55] glass rounded-2xl card-shadow px-4 py-2.5 flex items-center gap-3 max-w-[92vw]"
      role="status"
      aria-live="polite"
    >
      <span className="text-sm text-cyan-100 truncate">{item.label}</span>
      <button
        type="button"
        onClick={() => undoRef.current && undoRef.current()}
        className="tap px-3 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold flex items-center gap-1.5 flex-shrink-0"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        Undo
      </button>
      <button
        type="button"
        onClick={() => dismissRef.current && dismissRef.current()}
        aria-label="Dismiss"
        className="p-1 text-gray-400 hover:text-cyan-300 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="absolute left-3 right-3 bottom-1 h-0.5 bg-gray-600/50 rounded">
        <div className="h-full bg-cyan-400 rounded" style={{ width: `${left}%` }} />
      </div>
    </div>
  );
};

export default UndoToast;
